import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import { kpiApi } from "../api/live-client.js";
import type { ManagedKpi, UpdateKpiInput } from "../api/types.js";
import { kpiKeys } from "./kpiQueryKeys.js";

export interface UpdateKpiVars {
  readonly kpiId: string;
  readonly input: UpdateKpiInput;
}

/**
 * Edit a KPI's descriptive metadata (`PATCH /v1/kpis/:id`) — Tenant-Admin,
 * server-enforced.
 *
 * Invalidates three things:
 *  - the KPI's DETAIL, which is what the edit page and the KPI page both render;
 *  - the LISTS, because name, description and category all appear on the
 *    collection's rows and cards;
 *  - the CATEGORY OPTIONS, because that option set is the union of the installed
 *    catalog's categories and the codes the tenant's own KPIs carry — moving the
 *    last KPI off a tenant-only code removes it from the set, and adding one to a
 *    new code adds it. Missing this leaves the Create and Edit pickers offering a
 *    code nothing uses, or hiding one that is now in use.
 */
export function useUpdateKpi() {
  const config = useApiClient();
  const qc = useQueryClient();
  return useMutation<ManagedKpi, Error, UpdateKpiVars>({
    mutationFn: ({ kpiId, input }) => kpiApi.updateKpi(config, kpiId, input),
    onSuccess: (_data, { kpiId }) => {
      void qc.invalidateQueries({ queryKey: kpiKeys.detail(kpiId) });
      void qc.invalidateQueries({ queryKey: kpiKeys.lists() });
      void qc.invalidateQueries({ queryKey: kpiKeys.categoryOptions() });
    },
  });
}
